import { useCallback, useEffect, useRef, useState } from 'react';
import Button from './Button';
import ZoneDropdown from './ZoneDropdown';
import useFleetStore from '../../utils/store';
import { useIsWriteRestrictedUser } from "../../hooks/useIsWriteRestrictedUser";

const ZoneModal = ({
  title,
  subText,
  cancelText = "Cancel",
  submitText = "Submit",
  onCancel,
  onSubmit,
  shouldSetGloballySelectedFleet = true,
  zoneFilter,
  "data-testid": dataTestId = "zone-modal",
}) => {
  const isActionsDisabled = useIsWriteRestrictedUser();

  const { allPodData, selectedFleet, setSelectedFleet } = useFleetStore();
  const [selectedZone, setSelectedZone] = useState(selectedFleet);
  const [showFleetDropdown, setShowFleetDropdown] = useState(false);
  const dropdownFleetRef = useRef(null);

  const getDisplayedFleets = useCallback(() => {
    if (typeof zoneFilter === 'function') {
      return (allPodData?.zones || []).filter(zoneFilter);
    } else {
      return allPodData?.zones || [];
    }
  }, [allPodData?.zones, zoneFilter])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownFleetRef.current &&
        !dropdownFleetRef.current.contains(event.target)
      ) {
        setShowFleetDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectFleet = (fleet) => {
    setSelectedZone(fleet);
    if (shouldSetGloballySelectedFleet) {
      setSelectedFleet(fleet);
    }
  };

  const handleFleetDropdown = () => {
    setShowFleetDropdown((prev) => !prev);
  };

  useEffect(() => {
    const displayedFleets = getDisplayedFleets();
    if (displayedFleets.length > 0 && (!selectedZone || !displayedFleets.find(f => f.unid === selectedZone.unid))) {
      setSelectedZone(displayedFleets[0]);
    }
  }, [getDisplayedFleets, selectedZone, setSelectedZone]);

  return (
    <div className="overlayHolder bg-black/30 dark:bg-black/60 fixed inset-0 flex justify-center top-0 z-[999]" data-testid={dataTestId}>
      <div className="modalHlder bg-ivory dark:bg-darkThemeModalBg dark:shadow-darkThemeModalShadow rounded-2xl h-[300px] border border-black/10 w-full mx-5 sm:w-[400px] sm:mx-auto  my-auto p-4 lg:p-6 overflow-auto">
        <div className="modalContent h-full flex flex-col">
          <div className="flex flex-col gap-1 flex-1">
            <h2 className="modalTitle text-dark-grey dark:text-white text-lg leading-5 lg:text-xl lg:leading-6 font-semibold tracking-[0.03px]">{title}</h2>
            {subText && (
              <p className="text-dark-grey text-xs dark:text-white leading-4 tracking-[0.021px] font-normal w-full lg:w-11/12">{subText}</p>
            )}
          </div>
          <div className='flex flex-1'>
            <ZoneDropdown
              data-testid={`${dataTestId}-zone-dropdown`}
              fleets={getDisplayedFleets()}
              selectedFleet={selectedZone}
              onSelect={handleSelectFleet}
              show={showFleetDropdown}
              toggle={handleFleetDropdown}
              dropdownRef={dropdownFleetRef}
              AdditionalWrapperClass="border border-grey-300 rounded-xl w-full items-center justify-between p-4 mx-2 mt-5 shadow-appShadow"
              AdditionalChildClass={true}
              WrapperClass="w-full"
              childClass={true}
            />
          </div>
          <div className=" mt-6 sm:mt-10 actionsHolder flex-1 flex justify-end">
            <div className="flex items-center flex-wrap sm:flex-nowrap gap-4 lg:gap-6 justify-center sm:justify-end">
              <Button
                data-testid={`${dataTestId}-submit-button`}
                onClick={() => {
                  if (isActionsDisabled) return;
                  onSubmit(selectedZone);
                }}
                className={`flex justify-center items-center basis-24 shrink bg-theme px-5 py-3 text-white text-base font-semibold leading-5 tracking-[-0.32px] rounded-md h-10 hover:bg-theme/80 transition-all duration-300 ease-in-out ${
                  isActionsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
                disabled={isActionsDisabled}
              >
                {submitText}
              </Button>
              <Button data-testid={`${dataTestId}-cancel-button`} onClick={onCancel} className="flex justify-center items-center basis-24  shrink border border-theme px-5 py-3 text-theme text-base font-semibold leading-5 tracking-[-0.32px] rounded-md cursor-pointer h-10 hover:bg-theme hover:text-white transition-all duration-300 ease-in-out">
                {cancelText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoneModal;
